/**
 * `maxPurchasePerSlot` — the per-wristband, per-slot purchase cap.
 *
 * The backend enforces this on every mutation path (checkout, re-purchase,
 * group upload, transfer, reschedule) and answers 409
 * `ATTRACTION_PURCHASE_CAP_EXCEEDED`. Everything here is UX: clamp the stepper,
 * mark slots the customer personally can't take, and catch a breach before the
 * cashier reaches a payment sheet. The server stays authoritative — if this
 * math is ever wrong, the worst case is the old behaviour (a 409 at checkout).
 *
 * Semantics, mirrored from be_autopia_online's AttractionPurchaseCapService:
 *   -1  unlimited
 *    0  not purchasable
 *    N  at most N per wristband per (attraction, slot)
 *
 * The quota is per ATTRACTION, not per product — adult and child tickets for
 * the same ride share it — and refunded/consumed add-ons don't count.
 *
 * Ported verbatim (TS) from
 * app-pos-tiket-online/src/@core/utils/purchase-cap.js — keep the semantics
 * identical so the two apps agree with the backend.
 */

export const UNLIMITED = -1;

/** A held/cart line the cap math counts over. */
export interface CapLine {
  attractionId?: string | null;
  slotIds?: Array<string | null | undefined>;
}

/** Minimal held add-on shape (a live wristband add-on). */
export interface HeldAddOn {
  attractionId?: string | null;
  slotId?: string | null;
  status?: string | null;
  refundedAt?: string | null;
  [k: string]: unknown;
}

/** Minimal slot shape — only `id` is read by the cap math. */
export interface CapSlot {
  id?: string | null;
}

/** A product/add-on that inherits its attraction's cap. */
export interface CappedProduct {
  maxPurchasePerSlot?: number | null;
}

/** The cap a catalog product inherits from its attraction; absent = unlimited. */
export function capOfProduct(product: CappedProduct | null | undefined): number {
  const raw = product?.maxPurchasePerSlot;
  return raw == null ? UNLIMITED : Number(raw);
}

/** Does this cap actually bite? (`-1` never does.) */
export function isCapped(cap: number): boolean {
  return Number.isFinite(cap) && cap >= 0;
}

const keyOf = (attractionId: string | null | undefined, slotId: string | null | undefined): string =>
  `${String(attractionId)}::${slotId ? String(slotId) : ''}`;

/**
 * Count what a wristband ALREADY holds, keyed by (attraction, slot).
 * Only live add-ons count: a refunded one has released its quota, and a used
 * one (ENTER/EXIT) is spent — matching the backend's own tally.
 */
export function buildHeldCounts(addOns: HeldAddOn[] = []): Map<string, number> {
  const held = new Map<string, number>();
  for (const a of addOns || []) {
    if (!a || !a.attractionId) continue;
    if (a.refundedAt) continue;
    if (a.status && a.status !== 'AVAILABLE') continue;
    const k = keyOf(a.attractionId, a.slotId);
    held.set(k, (held.get(k) || 0) + 1);
  }
  return held;
}

/** Count what the CART currently assigns, keyed by (attraction, slot). */
export function buildCartCounts(lines: CapLine[] = []): Map<string, number> {
  const cart = new Map<string, number>();
  for (const line of lines || []) {
    if (!line || !line.attractionId) continue;
    for (const slotId of line.slotIds || []) {
      if (!slotId) continue;
      const k = keyOf(line.attractionId, slotId);
      cart.set(k, (cart.get(k) || 0) + 1);
    }
  }
  return cart;
}

/** How many more units of `attractionId` may land on `slotId`. */
export function remainingForSlot({
  cap,
  attractionId,
  slotId,
  held,
  cart,
}: {
  cap: number;
  attractionId: string | null | undefined;
  slotId: string | null | undefined;
  held?: Map<string, number> | null;
  cart?: Map<string, number> | null;
}): number {
  if (!isCapped(cap)) return Infinity;
  const k = keyOf(attractionId, slotId);
  const used = (held?.get(k) || 0) + (cart?.get(k) || 0);
  return Math.max(0, cap - used);
}

/**
 * The slotIds this wristband may no longer take for `attractionId`, used to
 * disable those slot options with a "limit reached" caption rather than a
 * capacity one — the slot may be wide open for everyone else.
 *
 * `excludeSlotIds` drops the units of the line being edited from the tally, so
 * a customer re-picking a slot isn't blocked by their own current choice.
 */
export function capReachedSlotIds({
  cap,
  attractionId,
  slots,
  held,
  cart,
  excludeSlotIds = [],
}: {
  cap: number;
  attractionId: string | null | undefined;
  slots?: CapSlot[] | null;
  held?: Map<string, number> | null;
  cart?: Map<string, number> | null;
  excludeSlotIds?: Array<string | null | undefined>;
}): Set<string> {
  const blocked = new Set<string>();
  if (!isCapped(cap) || !attractionId) return blocked;
  const excluded = new Map<string, number>();
  for (const sid of excludeSlotIds) {
    if (!sid) continue;
    excluded.set(String(sid), (excluded.get(String(sid)) || 0) + 1);
  }
  for (const slot of slots || []) {
    const sid = slot?.id;
    if (!sid) continue;
    const k = keyOf(attractionId, sid);
    const used = (held?.get(k) || 0) + (cart?.get(k) || 0) - (excluded.get(String(sid)) || 0);
    if (Math.max(0, cap - used) <= 0) blocked.add(String(sid));
  }
  return blocked;
}

/**
 * The most units of one product the customer could still buy — the sum of what
 * every open slot has left. With a cap of 1 and eight open slots, a wristband
 * holding one already can still buy seven; clamping to `cap` would be wrong.
 *
 * When the slot list hasn't loaded yet we return `cap` (the best case for a
 * single slot) rather than 0, so the stepper never locks up waiting on a fetch.
 */
export function maxAddableForProduct({
  cap,
  attractionId,
  slots,
  held,
  cart,
  ownSlotIds = [],
}: {
  cap: number;
  attractionId: string | null | undefined;
  slots?: CapSlot[] | null;
  held?: Map<string, number> | null;
  cart?: Map<string, number> | null;
  ownSlotIds?: Array<string | null | undefined>;
}): number {
  if (!isCapped(cap)) return Infinity;
  if (cap === 0) return 0;
  if (!attractionId) return Infinity;
  if (!Array.isArray(slots) || slots.length === 0) return cap;

  const own = new Map<string, number>();
  for (const sid of ownSlotIds) {
    if (!sid) continue;
    own.set(String(sid), (own.get(String(sid)) || 0) + 1);
  }

  // Discount this line's OWN units from each slot's usage, so the sum reads
  // "where could this line's units go", not "what's left after them". Adding
  // them back afterwards would double-count: the slots they occupy are already
  // part of the sum.
  let total = 0;
  for (const slot of slots) {
    const sid = slot?.id;
    if (!sid) continue;
    const k = keyOf(attractionId, sid);
    const used = (held?.get(k) || 0) + (cart?.get(k) || 0) - (own.get(String(sid)) || 0);
    total += Math.max(0, cap - used);
  }
  return total;
}

export interface CapViolationLine extends CapLine {
  cap?: number;
  attractionName?: string | null;
}

export interface CapViolation {
  attractionId: string;
  attractionName: string | null;
  slotId: string;
  limit: number;
  requested: number;
}

/**
 * Breaches in the CURRENT selection — the backstop for paths the per-slot
 * disabling can't cover, notably "apply this slot to all units" fanning more
 * units onto one slot than the cap allows.
 */
export function findCapViolations({
  lines = [],
  held,
}: {
  lines?: CapViolationLine[];
  held?: Map<string, number> | null;
}): CapViolation[] {
  const tally = new Map<string, CapViolation>();
  for (const line of lines) {
    if (!line || !line.attractionId || !isCapped(Number(line.cap))) continue;
    for (const slotId of line.slotIds || []) {
      if (!slotId) continue;
      const k = keyOf(line.attractionId, slotId);
      const cur = tally.get(k) || {
        attractionId: String(line.attractionId),
        attractionName: line.attractionName || null,
        slotId: String(slotId),
        limit: Number(line.cap),
        requested: held?.get(k) || 0,
      };
      cur.requested += 1;
      tally.set(k, cur);
    }
  }
  return [...tally.values()].filter((v) => v.requested > v.limit);
}

/** UI label for a slot a wristband has maxed out (distinct from sold-out). */
export const CAP_REACHED_LABEL = 'Batas tercapai';
